import { Button, ChatMessage, ChatThread, Notice } from '@bennows/boows-react';
import { useTranslation } from 'react-i18next';

import type { AssistantAction } from './assistant-api';
import { NavButton } from './NavButton';
import { ActionList } from './ActionList';
import type { ChatTurn } from './useAssistantChat';

interface Props {
  readonly turns: readonly ChatTurn[];
  readonly activeText: string | null;
  readonly actionState: {
    readonly overrides: Readonly<Record<string, AssistantAction>>;
    readonly conflicts: ReadonlySet<string>;
    readonly undoing: ReadonlySet<string>;
    readonly onUndo: (action: AssistantAction) => Promise<void>;
  };
  readonly onRetry: (turn: ChatTurn) => void;
}

/** The notice under a question whose turn did not end in an answer; every cause says what to do next. */
function TurnProblem({ turn, onRetry }: { turn: ChatTurn; onRetry: (turn: ChatTurn) => void }) {
  const { t } = useTranslation();
  const cause = turn.outcome === 'budget' ? 'budget' : turn.outcome === 'timeout' ? 'timeout' : 'failed';
  return (
    <Notice
      tone="danger"
      role={cause === 'timeout' ? 'status' : 'alert'}
      title={t(`assistant.error.${cause}.title`)}
      actions={
        cause === 'budget' ? (
          <NavButton to="/settings/billing" variant="secondary">
            {t('assistant.error.budget.topUp')}
          </NavButton>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => onRetry(turn)}>
            {t('assistant.error.retry')}
          </Button>
        )
      }
    >
      {t(`assistant.error.${cause}.text`)}
    </Notice>
  );
}

/**
 * The conversation as a polite log (spec 110 section 4.15.6): the member's questions and, once a turn is
 * terminal, its answer with the action list. A running turn is not in the log (progress is not read word
 * by word), so the finished answer is announced once.
 */
export function ChatView({ turns, activeText, actionState, onRetry }: Props) {
  const { t } = useTranslation();
  return (
    <ChatThread ariaLabel={t('assistant.thread')} busy={activeText !== null} className="ui-assistant__thread">
      {turns.map((turn) => (
        <div key={turn.id} className="ui-assistant__turn">
          <ChatMessage role="user" name={t('assistant.message.you')}>
            {turn.text}
          </ChatMessage>
          {turn.outcome === 'failed' || turn.outcome === 'budget' || turn.outcome === 'timeout' ? (
            <TurnProblem turn={turn} onRetry={onRetry} />
          ) : (
            <ChatMessage
              role="assistant"
              name={t('assistant.message.assistant')}
              after={
                <>
                  <p className="ui-assistant__ai-note">{t('assistant.message.aiWritten')}</p>
                  <ActionList actions={turn.actions} {...actionState} />
                </>
              }
            >
              {turn.answer ?? (turn.outcome === 'capped' ? t('assistant.error.capped') : t('assistant.error.noAnswer'))}
            </ChatMessage>
          )}
        </div>
      ))}
      {activeText !== null ? (
        <ChatMessage role="user" name={t('assistant.message.you')}>
          {activeText}
        </ChatMessage>
      ) : null}
    </ChatThread>
  );
}

